import { ChangeDetectorRef, Directive, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@klickdata/core/auth';
import { FormHelper } from '@klickdata/core/form';
import { MobileService } from '@klickdata/core/mobile';
import { AppScope, Resource, ResourceService, ResourceTypes } from '@klickdata/core/resource';
import { ResourceItem } from '@klickdata/core/resource-item';
import { ResourceBuildingItems } from '@klickdata/core/resource/src/types.enum';
import { User } from '@klickdata/core/user';
import { Observable, Subject, filter, first, map, of, switchMap, takeUntil } from 'rxjs';
import { ResourceEditBottomSheetComponent } from '../../resource-edit-bottom-sheet/resource-edit-bottom-sheet.component';

@Directive()
export abstract class ResourceItemDirective implements OnDestroy {
    @Input() selection: number[] = [];
    @Input()
    public item: ResourceItem;
    @Input()
    public set childGroup(childGroup: FormGroup) {
        this._childGroup = childGroup;
        if (childGroup.value.child_resource_id) {
            this.loading = true;
            this.resourceService
                .getResource(
                    childGroup.value.child_resource_id,
                    childGroup.value.item_type_value.indexOf('scorm') !== -1 ? 'scorm,tags' : 'tags'
                )
                .pipe(
                    takeUntil(this.destroy),
                    filter((res) => !!res)
                )
                .subscribe((resource) => {
                    this.selected = resource;
                    this.onResourceExist.emit(resource);
                    this.loading = false;
                    this.changeRef.markForCheck();
                });
        }
    }
    @Output() onResourceExist: EventEmitter<Resource> = new EventEmitter<Resource>();
    @Output() onSubmitThenRoute: EventEmitter<Resource> = new EventEmitter<Resource>();
    public get childGroup(): FormGroup {
        return this._childGroup;
    }
    protected _childGroup: FormGroup;

    protected destroy: Subject<boolean> = new Subject<boolean>();
    public loading: boolean;
    private _selected: Resource;
    public get selected(): Resource {
        return this._selected;
    }

    public set selected(value: Resource) {
        this._selected = value;
        this.afterResourceInit();
    }

    public user$: Observable<User>;
    private isMobile: boolean;
    ResourceTypes = ResourceTypes;
    AppScope = AppScope;
    ResourceBuildingItems = ResourceBuildingItems;
    changeMode = false;

    constructor(
        protected resourceService: ResourceService,
        protected changeRef: ChangeDetectorRef,
        protected dialog: MatDialog,
        protected authService: AuthService,
        protected mobileService: MobileService,
        protected bottomSheet: MatBottomSheet
    ) {
        this.user$ = this.authService.getUser().pipe(first());
        this.mobileService
            .isMobile()
            .pipe(takeUntil(this.destroy))
            .subscribe((isMobile) => (this.isMobile = isMobile));
    }
    onChangeMode(status: boolean) {
        this.changeMode = status;
        this.changeRef.markForCheck();
    }
    selectResource(resource: Resource) {
        this.selected = resource;
        const patch = {
            child_resource_id: resource.id,
            child_resource_published: resource.published,
            child_resource_language_id: resource.language_id,
        };
        // Inheritance of title and description when select test, If not defined by user.
        if (!this.childGroup.value.name) {
            patch['name'] = resource.title;
        }
        if (!this.childGroup.value.description) {
            patch['description'] = resource.bullets;
        }
        if (!this.childGroup.value.bullets) {
            patch['bullets'] = resource.bullets;
        }
        this.childGroup.patchValue(patch);
        this.afterResourceSelect();
        this.onResourceExist.emit(this.selected);
        FormHelper.markForm(this.childGroup);
        this.changeRef.markForCheck();
    }

    protected afterResourceSelect() {}
    protected afterResourceInit() {}

    prepareResourceFromItem(): Observable<Resource> {
        if (this.selected?.id) {
            return of(this.selected);
        }
        return this.resourceService.getResource(this.childGroup.value.child_resource_id).pipe(
            takeUntil(this.destroy),
            filter((res) => !!res)
        );
    }

    onEdit(index?: number) {
        this.prepareResourceFromItem()
            .pipe(
                map((res) => {
                    this.selected = res;
                    this.selected.scope_id = ResourceTypes.scopeByChildType(this.selected.type_id);
                    return this.selected;
                }),
                switchMap((res) =>
                    this.bottomSheet
                        .open(ResourceEditBottomSheetComponent, {
                            data: { resource: this.selected, openedMaterialItemIndex: index },
                            disableClose: true,
                        })
                        .afterDismissed()
                ),
                filter((res) => !!res),
                takeUntil(this.destroy)
            )
            .subscribe((result: { resource: Resource; editSource: boolean }) => {
                this.selectResource(result.resource);
                if (result.editSource) {
                    this.onSubmitThenRoute.emit(result.resource);
                }
            });
    }

    public ngOnDestroy(): void {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
